import { useState } from 'react';
import Tilt from 'react-parallax-tilt';
import { ShoppingBag, Heart, Star, Store, X, ArrowRight } from 'lucide-react';
import lyche from "../assets/Lychee Ice Cream.png";
import pineapple from "../assets/Pineapple.jpeg";
import pomegranate from "../assets/Pomegranate.jpeg";

const flavors = [
    {
        id: 1,
        name: "Lyche Delight",
        description: "Made with real lychee fruit.",
        price: "Tk.450",
        rating: 4.8,
        color: "bg-indigo-100",
        btnColor: "bg-indigo-600",
        img: lyche
    },
    {
        id: 2,
        name: "Pineapple Paradise",
        description: "Tropical pineapple with a hint of coconut.",
        price: "Tk.425",
        rating: 4.8,
        color: "bg-yellow-100",
        btnColor: "bg-yellow-500",
        img: pineapple
    },
    {
        id: 3,
        name: "Pomegranate Passion",
        description: "Tangy pomegranate with a sweet twist.",
        price: "Tk.425",
        rating: 4.7,
        color: "bg-red-100",
        btnColor: "bg-green-500",
        img: pomegranate
    }
];

const PopularFlavors = () => {
    const [selectedItem, setSelectedItem] = useState(null);

    const handleAddToCart = (flavorName) => {
        setSelectedItem(flavorName);
    };

    return (
        <section className="py-20 bg-white relative" id="flavors">
            <div className="max-w-7xl mx-auto px-6">
                
                {/* Section Header */}
                <div className="text-center mb-16">
                    <span className="text-pink-500 font-bold tracking-wider uppercase text-sm">Sweet Selections</span>
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-2">
                        Top Picks of the Week 🍦
                    </h2>
                    <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
                        These are the flavors that our customers are going crazy for! Grab them before they melt away.
                    </p>
                </div>

                {/* Grid of Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {flavors.map((iceCream) => (
                        <Tilt 
                            key={iceCream.id} 
                            tiltMaxAngleX={5} 
                            tiltMaxAngleY={5} 
                            scale={1.02} 
                            transitionSpeed={400}
                            className="h-full"
                        >
                            <div className={`relative group rounded-3xl p-6 h-full border border-white shadow-xl hover:shadow-2xl transition-all duration-300 ${iceCream.color}`}>
                                
                                {/* Favorite Heart Icon */}
                                <button className="absolute top-6 right-6 p-2 bg-white/60 backdrop-blur-sm rounded-full text-gray-400 hover:text-red-500 hover:bg-white transition-colors z-10">
                                    <Heart className="w-5 h-5" />
                                </button>

                                {/* Image Area */}
                                <div className="relative h-64 mb-6 flex items-center justify-center">
                                    <div className="absolute inset-0 bg-white/30 rounded-full filter blur-2xl transform group-hover:scale-110 transition-transform duration-500"></div>
                                    <img 
                                        src={iceCream.img} 
                                        alt={iceCream.name}
                                        className="w-52 h-52 object-cover rounded-full shadow-lg transform group-hover:-translate-y-4 group-hover:rotate-6 transition-all duration-500 z-10 border-4 border-white"
                                    />
                                </div>

                                {/* Content */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-start">
                                        <h3 className="text-2xl font-bold text-gray-800">{iceCream.name}</h3>
                                        <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-lg shadow-sm">
                                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                            <span className="font-bold text-sm">{iceCream.rating}</span>
                                        </div>
                                    </div>
                                    
                                    <p className="text-gray-600 text-sm line-clamp-2">
                                        {iceCream.description}
                                    </p>

                                    {/* Price & Add Button */}
                                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-black/5">
                                        <span className="text-2xl font-black text-gray-900">{iceCream.price}</span>
                                        
                                        <button 
                                            onClick={() => handleAddToCart(iceCream.name)}
                                            className={`${iceCream.btnColor} text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold shadow-md hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0 transition-all`}
                                        >
                                            <ShoppingBag className="w-4 h-4" />
                                            Add
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Tilt>
                    ))}
                </div>

            </div>

            {/* Fancy Modal Popup */}
            {selectedItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-fadeIn">
                    <div 
                        className="bg-white rounded-3xl max-w-md w-full p-6 text-center shadow-2xl border border-pink-100 relative transform transition-all animate-scaleUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button 
                            onClick={() => setSelectedItem(null)}
                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Top Icon Badge */}
                        <div className="w-16 h-16 bg-pink-100 text-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                            <Store className="w-8 h-8" />
                        </div>

                        {/* Text Content */}
                        <span className="inline-block px-3 py-1 bg-pink-50 text-pink-600 rounded-full text-xs font-bold tracking-wide uppercase mb-2">
                            Scooping Soon!
                        </span>
                        
                        <h3 className="text-2xl font-extrabold text-gray-900 mb-2">
                            Online Orders are Resting 🍦
                        </h3>
                        
                        <p className="text-gray-600 text-sm leading-relaxed mb-6">
                            We're currently refilling our freezer batches! Online delivery for <span className="font-semibold text-gray-800">{selectedItem}</span> is temporarily paused. Come visit our parlor in person!
                        </p>

                        {/* Action Buttons */}
                        <div className="space-y-2">
                            <button 
                                onClick={() => setSelectedItem(null)}
                                className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold shadow-lg shadow-pink-500/25 hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
                            >
                                Got it, thanks!
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default PopularFlavors;